import { round } from "helpers/numbers";
import { IPlanMeta } from "helpers/subscription-plans";
import { IPlanLimits } from "store/entities/application-settings/interfaces";
import { BillingCycle, ISubscription, Plan, Status } from "store/entities/subscriptions";

export const getIsGrandfathering = (
  plan: Plan,
  currentSubscription: ISubscription,
  newSubscription: ISubscription
): boolean =>
  plan === Plan.Team &&
  currentSubscription.plan === Plan.Team &&
  currentSubscription.billingCycle === newSubscription.billingCycle &&
  currentSubscription.status === Status.Active &&
  currentSubscription.pricePerAgent !== 0 &&
  (currentSubscription.pricePerAgent ?? NaN) / currentSubscription.billingCycle / 100 < 29;

export const getPrice = (
  planElement: IPlanMeta,
  isGrandfathering: boolean,
  subscription: ISubscription,
  newSubscription: ISubscription
): number => {
  // non-profit licenses have a fixed price of 0, so we display 0 only for their current active plan
  if (subscription.pricePerAgent === 0 && planElement.plan === subscription.plan) {
    return 0;
  }

  if (isGrandfathering) {
    return round((subscription.pricePerAgent ?? NaN) / subscription.billingCycle / 100, 2);
  }

  return planElement.price[newSubscription.billingCycle] || planElement.price[BillingCycle.Monthly];
};

export const getRecommendedPlanBasedOnUsage = ({
  teamsCount,
  forwardingAddressesCount,
  replyAddressesCount,
  fullyCustomDomainsCount,
  emailTemplatesCount,
  activeWorkflowsCount,
  activeCustomFieldsCount,
  sharedMacrosCount,
  standardPlansLimits,
}: {
  teamsCount: number;
  forwardingAddressesCount: number;
  replyAddressesCount: number;
  fullyCustomDomainsCount: number;
  emailTemplatesCount: number;
  activeWorkflowsCount: number;
  activeCustomFieldsCount: number;
  sharedMacrosCount: number;
  standardPlansLimits: Record<Plan, Record<keyof IPlanLimits, number>>;
}): Plan => {
  for (const plan of [Plan.Team, Plan.Business]) {
    const planLimits = standardPlansLimits[plan];

    if (
      teamsCount <= planLimits.maxTeams &&
      fullyCustomDomainsCount <= planLimits.maxEmailDomains &&
      emailTemplatesCount <= planLimits.maxTemplatesPerLicense &&
      activeWorkflowsCount <= planLimits.maxRulesActive &&
      activeCustomFieldsCount <= planLimits.maxCustomFieldsActive &&
      sharedMacrosCount <= planLimits.maxSharedMacros &&
      // we have to add 1 to the count of forwarding addresses because the default mailbox is not included in the list from the API
      forwardingAddressesCount + 1 <= planLimits.maxMailboxes &&
      // we have to add 1 to the count of reply addresses because the default reply address is not included in the list from the API
      replyAddressesCount + 1 <= planLimits.maxReplyAddresses
    ) {
      return plan;
    }
  }

  return Plan.Enterprise;
};
